import { RefObject, useLayoutEffect, useState } from 'react'

const MIN_SCROLL_PX = 160

export interface UseTableScrollViewportMaxHeightOptions {
  enabled: boolean
  scrollContainerRef: RefObject<HTMLElement | null>
  bottomPaginationRef: RefObject<HTMLElement | null>
  /** Space between grid scroll box and pagination (e.g. Tailwind `mt-5` = 20px). */
  marginBelowScrollPx: number
}

/**
 * Sets a pixel max-height on the table scroll box so the bottom pagination sits just above the
 * visual viewport bottom, independent of resolution.
 */
export function useTableScrollViewportMaxHeight({
  enabled,
  scrollContainerRef,
  bottomPaginationRef,
  marginBelowScrollPx,
}: UseTableScrollViewportMaxHeightOptions): number | undefined {
  const [maxPx, setMaxPx] = useState<number | undefined>(undefined)

  useLayoutEffect(() => {
    if (!enabled) {
      setMaxPx(undefined)
      return
    }

    let rafId: number | null = null

    const measure = () => {
      const scrollEl = scrollContainerRef.current
      const bottomEl = bottomPaginationRef.current
      if (!scrollEl || !bottomEl) return

      const vh = window.visualViewport?.height ?? window.innerHeight
      const top = scrollEl.getBoundingClientRect().top
      const bottomBarH = bottomEl.offsetHeight
      const buffer = 8

      const maxByViewport = vh - marginBelowScrollPx - bottomBarH - buffer
      const flowBased = vh - top - marginBelowScrollPx - bottomBarH - buffer
      const h = Math.min(flowBased, maxByViewport)

      setMaxPx(Math.max(MIN_SCROLL_PX, Math.floor(h)))
    }

    const scheduleMeasure = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        measure()
      })
    }

    measure()

    const ro = new ResizeObserver(scheduleMeasure)
    const scrollEl = scrollContainerRef.current
    const bottomEl = bottomPaginationRef.current
    if (scrollEl) ro.observe(scrollEl)
    if (bottomEl) ro.observe(bottomEl)
    ro.observe(document.documentElement)
    if (document.body) ro.observe(document.body)

    window.addEventListener('resize', scheduleMeasure)
    window.addEventListener('scroll', scheduleMeasure)
    window.visualViewport?.addEventListener('resize', scheduleMeasure)

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      ro.disconnect()
      window.removeEventListener('resize', scheduleMeasure)
      window.removeEventListener('scroll', scheduleMeasure)
      window.visualViewport?.removeEventListener('resize', scheduleMeasure)
    }
  }, [enabled, marginBelowScrollPx, scrollContainerRef, bottomPaginationRef])

  return enabled ? maxPx : undefined
}
